import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { FileText, Home, Upload, X, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { archivosApi, unidadesApi } from '../../../lib/api';

export const EditAdminDialog = ({ open, onOpenChange, unidad, onSaved }) => {
  const [form, setForm] = useState({
    tipo_administracion: 'full',
    porcentaje_administracion: 20,
    fecha_inicio_admin: '',
    observaciones_admin: '',
    contrato_administracion: null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && unidad) {
      setForm({
        tipo_administracion: unidad.tipo_administracion || 'full',
        porcentaje_administracion: unidad.porcentaje_administracion ?? (unidad.tipo_administracion === 'simple' ? 8 : 20),
        fecha_inicio_admin: unidad.fecha_inicio_admin || '',
        observaciones_admin: unidad.observaciones_admin || '',
        contrato_administracion: unidad.contrato_administracion || null,
      });
    }
  }, [open, unidad]);

  const handleTipoChange = (tipo) => {
    setForm(prev => ({
      ...prev,
      tipo_administracion: tipo,
      porcentaje_administracion: tipo === 'full' ? 20 : 8,
    }));
  };

  const handleContratoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'contrato_administracion');
      const res = await archivosApi.upload(fd);
      setForm(prev => ({
        ...prev,
        contrato_administracion: {
          url: res.data.url,
          nombre: file.name,
          uploaded_at: new Date().toISOString(),
        },
      }));
      toast.success('Contrato cargado');
    } catch (err) {
      console.error(err);
      toast.error('Error al subir el contrato');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveContrato = () => {
    setForm(prev => ({ ...prev, contrato_administracion: null }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        tipo_administracion: form.tipo_administracion,
        porcentaje_administracion: form.porcentaje_administracion,
        fecha_inicio_admin: form.fecha_inicio_admin || null,
        observaciones_admin: form.observaciones_admin || null,
        contrato_administracion: form.contrato_administracion,
      };
      // Modo bulk (ej. fila agregada Cowork Foresta): actualiza N unidades en una sola request.
      if (Array.isArray(unidad?.__bulk_ids) && unidad.__bulk_ids.length > 0) {
        const res = await unidadesApi.bulkUpdateAdministracion({
          unidad_ids: unidad.__bulk_ids,
          ...payload,
        });
        const n = res?.data?.matched ?? unidad.__bulk_ids.length;
        toast.success(`Administración actualizada (${n} unidades)`);
      } else {
        await unidadesApi.updateAdministracion(unidad.unidad_id, payload);
        toast.success('Administración actualizada');
      }
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-600" />
            Editar Administración — {unidad?.nombre_unidad}
            {Array.isArray(unidad?.__bulk_ids) && unidad.__bulk_ids.length > 0 && (
              <span className="text-xs font-normal text-slate-500 ml-1">
                ({unidad.__bulk_ids.length} unidades)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo de administración */}
          <div className="space-y-2">
            <Label>Tipo de administración</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_admin_edit"
                  checked={form.tipo_administracion === 'full'}
                  onChange={() => handleTipoChange('full')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm">Full (20%)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_admin_edit"
                  checked={form.tipo_administracion === 'simple'}
                  onChange={() => handleTipoChange('simple')}
                  className="w-4 h-4 text-green-600"
                />
                <span className="text-sm">Simple (8%)</span>
              </label>
            </div>
          </div>

          {/* Fee % */}
          <div className="space-y-1">
            <Label>Fee de administración (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={form.porcentaje_administracion}
              onChange={(e) => setForm(prev => ({ ...prev, porcentaje_administracion: parseFloat(e.target.value) || 0 }))}
              className="w-24"
              data-testid="edit-admin-fee-input"
            />
          </div>

          {/* Fecha inicio */}
          <div className="space-y-1">
            <Label>Fecha inicio de administración</Label>
            <Input
              type="date"
              value={form.fecha_inicio_admin}
              onChange={(e) => setForm(prev => ({ ...prev, fecha_inicio_admin: e.target.value }))}
              className="w-48"
              data-testid="edit-admin-fecha-input"
            />
          </div>

          {/* Contrato */}
          <div className="space-y-2">
            <Label>Contrato de administración</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
              {form.contrato_administracion?.url ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{form.contrato_administracion.nombre}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      href={form.contrato_administracion.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                      title="Descargar"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </a>
                    <label className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-600 bg-amber-50 rounded hover:bg-amber-100 cursor-pointer" title="Reemplazar">
                      <Upload className="w-3.5 h-3.5" />
                      Reemplazar
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleContratoUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    <Button type="button" variant="ghost" size="sm" onClick={handleRemoveContrato} title="Quitar">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center gap-2 cursor-pointer">
                  <Upload className="w-7 h-7 text-slate-400" />
                  <span className="text-sm text-slate-500">
                    {uploading ? 'Subiendo...' : 'Click para subir contrato'}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleContratoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Observaciones */}
          <div className="space-y-1">
            <Label>Observaciones</Label>
            <Textarea
              rows={3}
              value={form.observaciones_admin}
              onChange={(e) => setForm(prev => ({ ...prev, observaciones_admin: e.target.value }))}
              placeholder="Comentarios internos..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={handleSave} disabled={saving} data-testid="guardar-edit-admin-btn">
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAdminDialog;
